<template>
    <button
        class="c-button-wrapper btn"
        :class="`btn-${btnVariant} btn-${size} ${buttonClasses}`"
        :style="gradientStyle"
        :type="type"
        :disabled="disabled"
        v-bind="ddName ? { 'data-dd-action-name': ddName } : {}"
        @click="(e) => emit('click', e)"
        @mouseover="isHover = true"
        @mouseleave="isHover = false"
    >
        <template v-if="!$slots.default">
            <div v-if="icon && iconPosition === 'left'" class="icon-container" :class="{ 'mr-2': !!text }">
                <font-awesome-icon :icon="getVisibleIcon" />
            </div>
            <span :class="textClasses">{{ text }}</span>
            <div v-if="icon && iconPosition === 'right'" class="icon-container">
                <font-awesome-icon :icon="getVisibleIcon" class="absolute right-3" />
            </div>
        </template>
        <slot />
    </button>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import type { CButtonVariant } from '.';

const props = withDefaults(
    defineProps<{
        ddName?: string;
        text?: string;
        icon?: string | string[];
        hoverIcon?: string | string[];
        size?: string;
        variant?: CButtonVariant;
        disabled?: boolean;
        type?: 'button' | 'submit';
        iconPosition?: 'left' | 'right';
        gradientColors?: string[];
    }>(),
    {
        type: 'button',
        size: 'md',
        iconPosition: 'left',
        gradientColors: () => ['#7258e0', '#88c3d1']
    }
);

const emit = defineEmits<{
    (event: 'click', e: MouseEvent): void;
}>();

const isHover = ref(false);

const gradientStyle = computed(() => {
    if (btnVariant.value !== 'gradient') return {};
    return {
        'background-image': `linear-gradient(to right, ${props.gradientColors.join(', ')})`,
        border: 'none',
        color: '#fff'
    };
});

const btnVariant = computed(() => (props.variant?.length ? props.variant : 'empty'));

const getVisibleIcon = computed(() => {
    if (isHover.value && props.hoverIcon) {
        return props.hoverIcon;
    }
    return props.icon;
});

const buttonClasses = computed(() => {
    return props.iconPosition === 'right' ? 'd-flex align-items-center justify-content-center position-relative' : '';
});

const textClasses = computed(() => {
    return props.iconPosition === 'right' ? 'flex-grow-1 text-left' : '';
});
</script>

<style lang="scss" scoped>
@import '@/assets/css/utilities/functions';
.c-button-wrapper {
    font-weight: normal;
    &.btn,
    &:focus,
    &:hover {
        box-shadow: none !important;
    }
    &.btn-empty:hover,
    &.btn-outline-empty:hover {
        background-color: get-color('gray-200');
    }
    &.btn-outline-empty {
        border: 1px solid get-color('gray-light');
    }
    &.btn-icon {
        padding: 0.5rem;
        height: 21px;
        box-sizing: content-box;
        font-size: 0.875rem;
        .icon-container {
            width: 20px;
            height: 20px;
        }
    }
}
</style>
